<template>
    <div>
        <RichResponseHeadline>
            {{serviceRenderSet?.month}}
        </RichResponseHeadline>
        <RichResponseListItem
            v-if="serviceRenderSet"
            :title="serviceRenderSet?.title"
            :icon="serviceRenderSet?.icon"
            icon-type="date"
            :image="serviceRenderSet?.image"
            class="cohesive"
            @click="() => jobsiteDetailFor = serviceRenderSet?.jobsiteId"
        >
            <div>
                <ion-chip
                    class="small"
                >
                    {{ serviceRenderSet?.shiftType }}
                </ion-chip>

                {{ `${datetime.formatKapitelDateTime(serviceRenderSet?.begin ?? '', datetime.kapitelDateTimeFormatTime)} - ${datetime.formatKapitelDateTime(serviceRenderSet?.until ?? '', datetime.kapitelDateTimeFormatTime)} Uhr` }}
            </div>
        </RichResponseListItem>
        <RichResponseListItem
            v-if="directDemandHistory"
            icon=""
            icon-type="text"
            class="cohesive condensed history"
        >
            <ul>
                <li
                    v-for="entry in historyRenderSet"
                >
                    {{ entry.status }} am {{ entry.date }}
                </li>
            </ul>
        </RichResponseListItem>

        <div
            class="section-actions"
        >
            <ion-button
                @click="onDecline"
                expand="block"
                color="light"
                :disabled="isAccepting || isDeclining"
            >
                <ion-spinner name="crescent" color="primary" v-if="isDeclining" />
                Ablehnen
            </ion-button>
            <ion-button
                @click="onAccept"
                expand="block"
                color="primary"
                :disabled="isAccepting || isDeclining"
            >
                <ion-spinner name="crescent" color="light" v-if="isAccepting" />
                Dienst annehmen
            </ion-button>
        </div>

        <JobsiteDetails v-model="jobsiteDetailFor"></JobsiteDetails>
    </div>
</template>

<script setup lang="ts">
import {IonButton, IonChip, IonSpinner} from "@ionic/vue";
import {computed, ref, Ref} from "vue";
import {DataLoader} from "@/helper/dataLoader";
import RichResponseListItem from "@/views/Chat/RichResponses/components/RichResponseListItem.vue";
import {
    AInesAssistantType,
    AInesRichResponseType,
    Booking,
    Demand,
    DirectDemand,
    DirectDemandLogEntry
} from "@/graphql/generated/graphql";
import {kapitelErrorHandler} from "@/helper/error";
import {
    acceptDirectDemand,
    declineDirectDemand,
    fetchDirectDemand,
    fetchDirectDemandHistory
} from "@/helper/chat/directDemand";
import {getImageForJobsite} from "@/helper/jobsite";
import {fetchDayForDate} from "@/helper/day";
import datetime from "@/helper/datetime/datetime";
import {presentScriptedMessage, sendUserMessage} from "@/helper/chat/chatBL";
import RichResponseHeadline from "@/views/Chat/RichResponses/components/RichResponseHeadline.vue";
import {formatMonthFromKapitelDate, getYearFromKapitelDate} from "@/helper/datetime/date";
import JobsiteDetails from "@/views/JobsiteDetails.vue";
import {useGlobalEmitter} from "@/helper/emitter";
import {probabilityCallback} from "@/helper/chance";
import {scriptedMessages} from "@/helper/chat/scriptedMessage";
import {RichContent} from "@/helper/chat/richResponses";

const props = defineProps({
    initData: {
        type: String,
        required: true
    }
})
const directDemand: Ref<DirectDemand|undefined> = ref(undefined)
const directDemandHistory: Ref<DirectDemandLogEntry[]> = ref([])
const booking: Ref<Booking|undefined> = ref(undefined)
const jobsiteDetailFor:Ref<number|undefined> = ref(undefined)

const serviceRenderSet = computed(() => {
    const dataSource:Demand|DirectDemandLogEntry|Booking = booking.value || directDemand.value?.demand || directDemandHistory.value[0]
    const titlePrefix = booking.value
        ? 'Dienst'
        : directDemand.value
            ? 'Direktanfrage'
            : 'Ehemalige Direktanfrage'

    if (!dataSource) {
        return
    }

    return {
        title: `${titlePrefix}: ${dataSource.jobsite?.abbreviation}`,
        icon: dataSource.date,
        image: getImageForJobsite(dataSource.jobsite || undefined),
        shiftType: dataSource.shiftType,
        begin: dataSource.begin,
        until: dataSource.until,
        month: formatMonthFromKapitelDate(dataSource.date) + ' ' + getYearFromKapitelDate(dataSource.date),
        jobsiteId: dataSource.jobsite?.id,
    }
})

const historyRenderSet = computed(() => {
    const transaction2Human = {
        'insert': 'Erstellt',
        'remove': 'Zurückgezogen'
    }

    return (directDemandHistory.value || []).map(entry => {
        return {
            date: datetime.formatKapitelDateTime(entry.timestamp ?? '', datetime.kapitelDateTimeFormatDefault) + ' Uhr',
            status: transaction2Human[entry.auditorTransactionStatus as 'insert' | 'remove']
        }
    })
})

const loader = new DataLoader(
    async () => {

        if(props.initData){
            const directDemandId = Number(props.initData)

            const [directDemandResponse, directDemandHistoryResponse] = await Promise.all([
                fetchDirectDemand(directDemandId),
                fetchDirectDemandHistory(directDemandId),
            ])

            directDemand.value = directDemandResponse
            directDemandHistory.value = directDemandHistoryResponse ?? []

            if(directDemandHistory.value.length > 1){
                const date = datetime.convertDateTime2Date(directDemandHistory.value[0].begin);
                const day = await fetchDayForDate(date)
                if(day?.booking){
                    booking.value = day.booking
                }
            }
        }
    }
)

loader.load()

const isAccepting = ref(false)
const isDeclining = ref(false)

const onDecline = async () => {
    if(!directDemand.value || !directDemand.value.id){
        kapitelErrorHandler("Direct demand not set.")
        return
    }

    try {
        isDeclining.value = true;
        await declineDirectDemand(directDemand.value.id)
        // sendUserMessage(`Vielen Dank, ich habe über das Interface die Direktanfrage abgelehnt. Schau bitte nach, ob das Ablehnen geklappt hat, indem du nachsiehst wie der ${directDemand.value.date} jetzt aussieht, und mir den Tageskalender anzeigst.`, {messageExpertAssistant: AInesAssistantType.Availability, isScriptedContent: true})
        const message = scriptedMessages.afterDeclineDirectDemand
        message.richContents = [{
            type: AInesRichResponseType.DayCalendar,
            initData: directDemand.value.date,
        } as RichContent]
        presentScriptedMessage(message)
    } catch (e) {
        kapitelErrorHandler(e)
    } finally {
        isDeclining.value = false;
    }

}

const onAccept = async () => {
    if(!directDemand.value || !directDemand.value.id){
        kapitelErrorHandler("Direct demand not set.")
        return
    }

    try {
        isAccepting.value = true;
        booking.value = await acceptDirectDemand(directDemand.value.id)
        if (booking.value) {
            probabilityCallback(0.2, () => useGlobalEmitter().emit("FireFireworks"))
            sendUserMessage(
                `Vielen Dank, ich konnte über das Interface die Direktanfrage annehmen. Zeig mir jetzt den Tageskalender vom ${directDemand.value.date} Tag an.`,
                {messageExpertAssistant: AInesAssistantType.Availability, isScriptedContent: true})
        }
    } catch (e) {
        loader.reload()
    } finally {
        isAccepting.value = false;
    }
}
</script>
<style lang="scss" scoped>
.section-actions {
    width: 100%;
    display: flex;
    margin-top: 1em;

    ion-button {
        flex: 1 1 100%;
        margin: 0;

        &:not(:last-child) {
            margin-right: 1rem;
        }
    }
}

.history {
    ul {
        margin: 0;
        padding: 0;
        list-style-type: none;
        margin-bottom: 0.5em;
    }
}
</style>
